import React, { useEffect, useState } from "react";
import Card from "../../../components/ui/card-snippet";
import {
  respEnum,
  TransactionSourceEnum,
} from "../../../constants/appConstant";
import Common from "../../../helper/common";
import {
  AppInput,
  AppSearchSectionSelect,
  AppSelect,
} from "../../../components/appComponents";
import Utility from "../../../helper/utilityHelper";
import { Button } from "../../../components/ui/button";
import common from "../../../helper/common";
import { Eye, X } from "lucide-react";

export default function Balance() {
  const firstDayOfMonth = common.moment
    .utc()
    .startOf("month")
    .format("YYYY-MM-DD");
  const lastDayOfMonth = common.moment
    .utc()
    .endOf("month")
    .startOf("day")
    .format("YYYY-MM-DD");

  const [accountDetails, setAccountDetails] = useState([]);
  const [startDate, setStartDate] = useState(firstDayOfMonth);
  const [tillDate, setTillDate] = useState(lastDayOfMonth);
  const [clubAccountId, setClubAccountId] = useState(null);
  const [paymentTypeList, setPaymentTypeList] = useState([]);
  const [paymentGateway, setPaymentGateway] = useState([]);
  const [paymentGatewayId, setPaymentGatewayId] = useState(null);
  const [transactionSource, setTransactionSource] = useState(null);
  const [showFullAmount, setShowFullAmount] = useState(null);
  const [hoveredAmount, setHoveredAmount] = useState(null);

  useEffect(() => {
    getClubPaymentTypes();
    getPaymentGateway();
    fetchAccountBalance();
  }, []);

  useEffect(() => {
    fetchAccountBalance();
  }, [startDate, tillDate, clubAccountId, paymentGatewayId, transactionSource]);

  const resetFilters = () => {
    setStartDate(firstDayOfMonth);
    setTillDate(lastDayOfMonth);
    setClubAccountId(null);
    setPaymentGatewayId(null);
    setTransactionSource(null);
  };

  const getClubPaymentTypes = async () => {
    const response = await Common.ApiService.getInstance().request(
      "GetClubPaymentTypes?isForReport=false"
    );

    if (response?.status === respEnum.Success) {
      const paymentTypes = response?.data ?? [];
      const selectOption = {
        label: "",
        options: [{ value: null, label: "Select" }],
      };
      setPaymentTypeList([selectOption, ...paymentTypes]);
    }
  };

  const getPaymentGateway = async () => {
    const response = await Common.ApiService.getInstance().request(
      "GetClubPaymentGateways"
    );

    if (response?.status == respEnum.Success) {
      const PaymentGateway = response?.data ?? [];
      const selectOption = {
        label: "",
        options: [{ value: null, label: "Select" }],
      };
      setPaymentGateway([selectOption, ...PaymentGateway]);
    }
  };

  const fetchAccountBalance = async () => {
    if (startDate && tillDate && startDate > tillDate) {
      Common.showToast("End Time must be greater", "Error", "info");
      return;
    }
    if (paymentGatewayId?.value && clubAccountId?.value) {
      Common.showToast(
        "You Can't Select Both Club Accounts and Payment Gateway ",
        "Error",
        "info"
      );
      return;
    }
    const params = new URLSearchParams();

    if (startDate) {
      params.append("startDateTime", startDate);
    }

    if (tillDate) {
      params.append("tillDate", tillDate);
    }

    if (clubAccountId?.value) {
      params.append("clubAccountId", clubAccountId.value);
    }

    if (paymentGatewayId?.value) {
      params.append("paymentGatewayId", paymentGatewayId.value);
    }

    if (transactionSource !== null) {
      params.append("transactionSource", transactionSource);
    }
    console.log("Params:", params.toString());
    const response = await Common.ApiService.getInstance().request(
      `GetPaymentSummary?${params.toString()}`
    );

    if (response?.status === respEnum.Success) {
      setAccountDetails(response.data[0] || []);
    }
  };

  return (
    <div className="space-y-6 mb-6">
      <style>
        {`
                    .paddingleft6 {
                        padding-left: 6px!important;
                    }

                    @media screen and (max-width: 768px) {
                        .paddingleft6 {
                            padding-left: 0px !important;
                        }
                             .paddingall {
                            padding-left: 10px !important;
                            padding-right: 10px !important;
                        }
                    }
                `}
      </style>

      <Card title="Account Balance">
        <div className="grid grid-cols-1 sm:grid-cols-6 gap-4 mt-5 paddingall">
          <AppInput
            type={"date"}
            value={startDate}
            onChange={(e) => {
              const selectedstartDate = e;
              if (tillDate && selectedstartDate > tillDate) {
                Common.showToast(
                  "Start Time must be lesser than End Time",
                  "Error",
                  "info"
                );
                setStartDate(null);
              } else {
                setStartDate(selectedstartDate);
              }
            }}
            label="From"
            className="mb-3 col-span-1 paddingleft6"
          />

          <AppInput
            type={"date"}
            value={tillDate}
            onChange={(e) => {
              const selectedEndDate = e;
              if (selectedEndDate < startDate) {
                Common.showToast("End Time must be greater", "Error", "info");
                setTillDate("");
              } else {
                setTillDate(selectedEndDate);
              }
            }}
            label="Till"
            className="mb-3 col-span-1"
          />

          <AppSearchSectionSelect
            value={clubAccountId}
            onChange={setClubAccountId}
            label="Account"
            data={paymentTypeList}
            className="mb-5 col-span-1"
          />

          <AppSearchSectionSelect
            value={paymentGatewayId}
            onChange={setPaymentGatewayId}
            label={"Payment Gateway"}
            data={paymentGateway}
            className="mb-5 col-span-1"
          />

          <AppSelect
            value={transactionSource}
            onChange={setTransactionSource}
            label="Transaction Source"
            data={[
              { id: null, name: "Select" },
              ...Common.Utility.enumToArray(TransactionSourceEnum),
            ]}
            className="mb-5 col-span-1"
          />

          <Button
            className="lg:mt-6 h-8 mb-6 w-[120px] px-2 col-span-1"
            onClick={resetFilters}
          >
            Reset Filter
          </Button>
        </div>
      </Card>

      {/* Results Table */}
      {(!accountDetails || Object.keys(accountDetails).length > 0) && (
        <Card title="">
          <table className="min-w-full divide-y divide-gray-200 border">
            <thead className="bg-gray-50">
              <tr>
                <th
                  scope="col"
                  colSpan={2}
                  className="px-6 py-3 text-center text-sm font-bold text-gray-700 uppercase tracking-wider"
                >
                  Payments
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200 font-bold">
              {Object.entries(accountDetails).map(([key, value]) => {
                const keyMapping = {
                  paymentReceived: "Received",
                  paymentRefundable: "Refundable",
                  paymentRefunded: "Refunded",
                };

                const displayKey = keyMapping[key] || key.replace(/_/g, " ");

                return (
                  <tr key={key}>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700 text-center">
                      {displayKey}
                    </td>

                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                      <div className="flex items-center gap-2 relative overflow-visible">
                        <span className="text-sm md:text-base font-medium">
                          {Utility.formatNumber(value)}
                        </span>

                        <div className="relative overflow-visible">
                          <button
                            onClick={() =>
                              setShowFullAmount(
                                showFullAmount === key ? null : key
                              )
                            }
                            onMouseEnter={() => setHoveredAmount(key)}
                            onMouseLeave={() => setHoveredAmount(null)}
                            className="p-1 hover:bg-gray-100 rounded-full transition-colors relative z-10"
                          >
                            <Eye className="w-3 h-3 md:w-4 md:h-4 text-gray-500" />
                          </button>

                          {(showFullAmount === key ||
                            hoveredAmount === key) && (
                            <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 bg-white shadow-lg rounded-lg p-2 md:p-3 border border-gray-200 z-[9999] whitespace-nowrap min-w-max">
                              <div className="flex items-center gap-2">
                                <span className="text-xs md:text-sm font-medium">
                                  {Utility.formatNumberWithCommas(value)}
                                </span>
                                <button
                                  onClick={() => setShowFullAmount(null)}
                                  className="p-1 hover:bg-gray-100 rounded-full"
                                >
                                  <X className="w-2 h-2 md:w-3 md:h-3 text-gray-500" />
                                </button>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </Card>
      )}
    </div>
  );
}
